// src/components/modals/CreatePriorityModal.tsx
import React, { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';

interface CreatePriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPriorityCreated?: (priority: { name: string; color: string }) => void;
}

const colorOptions = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Orange
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#EC4899', // Pink
  '#F97316', // Orange (darker)
  '#84CC16', // Lime
  '#A855F7', // Violet
  '#6B7280', // Gray
];

export const CreatePriorityModal: React.FC<CreatePriorityModalProps> = ({
  isOpen,
  onClose,
  onPriorityCreated
}) => {
  const [priorityName, setPriorityName] = useState('');
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!priorityName.trim()) return;

    // Here you would typically save the priority to your backend
    if (onPriorityCreated) {
      onPriorityCreated({
        name: priorityName.trim(),
        color: selectedColor
      });
    }

    // Reset form and close modal
    setPriorityName('');
    setSelectedColor(colorOptions[0]);
    onClose();
  };

  const handleClose = () => {
    setPriorityName('');
    setSelectedColor(colorOptions[0]);
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title="Add Priority"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-6 px-4">
        {/* Priority Name Input */}
        <div>
          <Input
            label="Priority Name"
            placeholder="Moderate"
            value={priorityName}
            onChange={(e) => setPriorityName(e.target.value)}
            required
            autoFocus
          />
        </div>

        {/* Color Selection */}
        <div>
          <div className="mb-3">
            <h3 className="text-sm font-medium text-gray-900">Color</h3>
          </div>
          
          {/* Color Options */}
          <div className="flex flex-wrap gap-3">
            {colorOptions.map((color) => (
              <button
                key={color}
                type="button"
                className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                  selectedColor === color 
                    ? 'border-gray-900 scale-110' 
                    : 'border-transparent hover:border-gray-400'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setSelectedColor(color)}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button 
            type="button" 
            variant="secondary" 
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary" 
            disabled={!priorityName.trim()}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreatePriorityModal;